/**
 * Pricing Utilities
 *
 * Centralizes the price ID mapping logic that was previously scattered
 * and confusingly named in PaymentSelection.js.
 *
 * NAMING CLARIFICATION:
 * The original code had confusing naming:
 * - BASIC_PLANS (env: REACT_APP_BASIC_*) actually refers to "Solo" tier ($6)
 * - B_PLANS (env: REACT_APP_B_*) refers to "Basic" tier ($3)
 *
 * This utility uses clear, consistent naming:
 * - 'basic' = Basic tier ($3/month in US)
 * - 'solo' = Solo tier ($6/month in US)
 * - 'pro' = Pro tier ($19/month in US)
 */

// Aero Stripe billing portal configuration ID.
// Run functions/create-portal-config.js to generate this, then paste the bpc_xxx ID here.
export const AERO_PORTAL_CONFIG = 'bpc_1TA94JJyvkMmBNuRFlreDS2K';

// Country groupings for pricing tiers
// These determine which price bucket a user falls into based on their country
const PRICING_TIERS = {
  // $1.50 base (Basic monthly) - emerging markets, strong tier
  THREE_DOLLAR: ['PH', 'MY', 'CN', 'AR', 'BR'],

  // $2.50 base (Basic monthly) - developed markets with some parity
  FIVE_DOLLAR: ['IE', 'GB', 'FR', 'JP', 'KR', 'IL', 'IT'],

  // $2 base (Basic monthly) - mid-tier markets
  FOUR_DOLLAR: ['TW', 'ES', 'PT', 'PL'],

  // $1 base (Basic monthly) - strongest discount tier
  TWO_DOLLAR: ['TH', 'VN', 'ID', 'IN', 'NG']

  // Default (US) = $3 base
};

/**
 * Gets the pricing tier for a country
 * @param {string} countryCode - Two-letter country code (e.g., "US", "PH")
 * @returns {string} - Tier key for pricing lookup (e.g., "US", "PH", "TH", "TW", "JP")
 */
export function getPricingTier(countryCode) {
  const code = countryCode?.toUpperCase() || 'US';

  if (PRICING_TIERS.THREE_DOLLAR.includes(code)) {
    return 'PH'; // Use PH pricing
  }
  if (PRICING_TIERS.FIVE_DOLLAR.includes(code)) {
    return 'JP'; // Use JP pricing
  }
  if (PRICING_TIERS.FOUR_DOLLAR.includes(code)) {
    return 'TW'; // Use TW pricing
  }
  if (PRICING_TIERS.TWO_DOLLAR.includes(code)) {
    return 'TH'; // Use TH pricing
  }

  return 'US'; // Default US pricing
}

/**
 * Gets the correct Stripe price ID for a plan, region, and interval
 * @param {string} planKey - 'basic', 'solo', or 'pro'
 * @param {string} pricingTier - Pricing tier from getPricingTier ('US', 'PH', 'TH', 'TW', 'JP')
 * @param {string} interval - 'monthly' or 'yearly'
 * @returns {string} - The Stripe price ID from environment variables
 */
export function getPriceId(planKey, pricingTier, interval) {
  const plan = planKey.toLowerCase();
  const tier = pricingTier.toUpperCase();
  const isYearly = interval === 'yearly';

  // Price ID environment variable mapping
  // The env vars have inconsistent naming, so we map them explicitly

  const priceIdMap = {
    // US (default) pricing
    US: {
      basic: {
        monthly: process.env.REACT_APP_B_MONTHLY,
        yearly: process.env.REACT_APP_B_YEARLY
      },
      solo: {
        monthly: process.env.REACT_APP_BASIC_MONTHLY,
        yearly: process.env.REACT_APP_BASIC_YEARLY
      },
      pro: {
        monthly: process.env.REACT_APP_PRO_MONTHLY,
        yearly: process.env.REACT_APP_PRO_YEARLY
      }
    },

    // PH pricing (THREE_DOLLAR tier)
    PH: {
      basic: {
        monthly: process.env.REACT_APP_BASIC_MONTHLY_THREE,
        yearly: process.env.REACT_APP_BASIC_YEARLY_THREE
      },
      solo: {
        monthly: process.env.REACT_APP_SOLO_MONTHLY_THREE,
        yearly: process.env.REACT_APP_SOLO_YEARLY_THREE
      },
      pro: {
        monthly: process.env.REACT_APP_PRO_MONTHLY_THREE,
        yearly: process.env.REACT_APP_PRO_YEARLY_THREE
      }
    },

    // TH pricing (TWO_DOLLAR tier)
    TH: {
      basic: {
        monthly: process.env.REACT_APP_BASIC_MONTHLY_TWO,
        yearly: process.env.REACT_APP_BASIC_YEARLY_TWO
      },
      solo: {
        monthly: process.env.REACT_APP_SOLO_MONTHLY_TWO,
        yearly: process.env.REACT_APP_SOLO_YEARLY_TWO
      },
      pro: {
        monthly: process.env.REACT_APP_PRO_MONTHLY_TWO,
        yearly: process.env.REACT_APP_PRO_YEARLY_TWO
      }
    },

    // TW pricing (FOUR_DOLLAR tier)
    TW: {
      basic: {
        monthly: process.env.REACT_APP_BASIC_MONTHLY_FOUR,
        yearly: process.env.REACT_APP_BASIC_YEARLY_FOUR
      },
      solo: {
        monthly: process.env.REACT_APP_SOLO_MONTHLY_FOUR,
        yearly: process.env.REACT_APP_SOLO_YEARLY_FOUR
      },
      pro: {
        monthly: process.env.REACT_APP_PRO_MONTHLY_FOUR,
        yearly: process.env.REACT_APP_PRO_YEARLY_FOUR
      }
    },

    // JP pricing (FIVE_DOLLAR tier)
    JP: {
      basic: {
        monthly: process.env.REACT_APP_BASIC_MONTHLY_FIVE,
        yearly: process.env.REACT_APP_BASIC_YEARLY_FIVE
      },
      solo: {
        monthly: process.env.REACT_APP_SOLO_MONTHLY_FIVE,
        yearly: process.env.REACT_APP_SOLO_YEARLY_FIVE
      },
      pro: {
        monthly: process.env.REACT_APP_PRO_MONTHLY_FIVE,
        yearly: process.env.REACT_APP_PRO_YEARLY_FIVE
      }
    }
  };

  // Get the price ID from the map, fallback to US if tier not found
  const tierPrices = priceIdMap[tier] || priceIdMap.US;
  const planPrices = tierPrices[plan];

  if (!planPrices) {
    console.warn(`Unknown plan: ${plan}`);
    return null;
  }

  return isYearly ? planPrices.yearly : planPrices.monthly;
}

/**
 * Maps a Stripe price ID to a plan key
 * Useful for determining the user's current plan from their subscription
 *
 * @param {string} priceId - Stripe price ID from subscription
 * @returns {string|null} - Plan key: 'basic', 'solo', or 'pro', or null if not found
 */
export function getPlanKeyFromPriceId(priceId) {
  if (!priceId) return null;

  // Build a reverse mapping from all known price IDs
  // This includes all regional variants

  // Basic plan price IDs
  const basicPriceIds = [
    process.env.REACT_APP_B_MONTHLY,
    process.env.REACT_APP_B_YEARLY,
    process.env.REACT_APP_BASIC_MONTHLY_TWO,
    process.env.REACT_APP_BASIC_YEARLY_TWO,
    process.env.REACT_APP_BASIC_MONTHLY_THREE,
    process.env.REACT_APP_BASIC_YEARLY_THREE,
    process.env.REACT_APP_BASIC_MONTHLY_FOUR,
    process.env.REACT_APP_BASIC_YEARLY_FOUR,
    process.env.REACT_APP_BASIC_MONTHLY_FIVE,
    process.env.REACT_APP_BASIC_YEARLY_FIVE
  ];

  // Solo plan price IDs (confusingly named BASIC_* in env for US)
  const soloPriceIds = [
    process.env.REACT_APP_BASIC_MONTHLY,  // US Solo
    process.env.REACT_APP_BASIC_YEARLY,   // US Solo
    process.env.REACT_APP_SOLO_MONTHLY_TWO,
    process.env.REACT_APP_SOLO_YEARLY_TWO,
    process.env.REACT_APP_SOLO_MONTHLY_THREE,
    process.env.REACT_APP_SOLO_YEARLY_THREE,
    process.env.REACT_APP_SOLO_MONTHLY_FOUR,
    process.env.REACT_APP_SOLO_YEARLY_FOUR,
    process.env.REACT_APP_SOLO_MONTHLY_FIVE,
    process.env.REACT_APP_SOLO_YEARLY_FIVE
  ];

  // Pro plan price IDs
  const proPriceIds = [
    process.env.REACT_APP_PRO_MONTHLY,
    process.env.REACT_APP_PRO_YEARLY,
    process.env.REACT_APP_PRO_MONTHLY_TWO,
    process.env.REACT_APP_PRO_YEARLY_TWO,
    process.env.REACT_APP_PRO_MONTHLY_THREE,
    process.env.REACT_APP_PRO_YEARLY_THREE,
    process.env.REACT_APP_PRO_MONTHLY_FOUR,
    process.env.REACT_APP_PRO_YEARLY_FOUR,
    process.env.REACT_APP_PRO_MONTHLY_FIVE,
    process.env.REACT_APP_PRO_YEARLY_FIVE
  ];

  if (basicPriceIds.includes(priceId)) return 'basic';
  if (soloPriceIds.includes(priceId)) return 'solo';
  if (proPriceIds.includes(priceId)) return 'pro';

  return null;
}

/**
 * Gets the billing interval from a price ID
 * @param {string} priceId - Stripe price ID
 * @returns {string|null} - 'monthly' or 'yearly', or null if not found
 */
export function getIntervalFromPriceId(priceId) {
  if (!priceId) return null;

  // All yearly price IDs
  const yearlyPriceIds = [
    process.env.REACT_APP_B_YEARLY,
    process.env.REACT_APP_BASIC_YEARLY,
    process.env.REACT_APP_PRO_YEARLY,
    process.env.REACT_APP_BASIC_YEARLY_TWO,
    process.env.REACT_APP_SOLO_YEARLY_TWO,
    process.env.REACT_APP_PRO_YEARLY_TWO,
    process.env.REACT_APP_BASIC_YEARLY_THREE,
    process.env.REACT_APP_SOLO_YEARLY_THREE,
    process.env.REACT_APP_PRO_YEARLY_THREE,
    process.env.REACT_APP_BASIC_YEARLY_FOUR,
    process.env.REACT_APP_SOLO_YEARLY_FOUR,
    process.env.REACT_APP_PRO_YEARLY_FOUR,
    process.env.REACT_APP_BASIC_YEARLY_FIVE,
    process.env.REACT_APP_SOLO_YEARLY_FIVE,
    process.env.REACT_APP_PRO_YEARLY_FIVE
  ];

  return yearlyPriceIds.includes(priceId) ? 'yearly' : 'monthly';
}

/**
 * Get all supported countries for testing
 * Grouped by pricing tier for easier navigation
 * @returns {Array} Array of country objects with code, name, tier, and tierLabel
 */
export function getSupportedCountries() {
  return [
    // Default tier ($6 USD base for Solo)
    { code: 'US', name: 'United States', tier: 'US', tierLabel: '$6 (Default)' },
    { code: 'DE', name: 'Germany', tier: 'US', tierLabel: '$6 (Default)' },
    { code: 'UA', name: 'Ukraine', tier: 'US', tierLabel: '$6 (Default)' },
    { code: 'AU', name: 'Australia', tier: 'US', tierLabel: '$6 (Default)' },
    { code: 'CA', name: 'Canada', tier: 'US', tierLabel: '$6 (Default)' },

    // $5 tier (FIVE_DOLLAR countries)
    { code: 'IE', name: 'Ireland', tier: 'JP', tierLabel: '$5' },
    { code: 'GB', name: 'United Kingdom', tier: 'JP', tierLabel: '$5' },
    { code: 'FR', name: 'France', tier: 'JP', tierLabel: '$5' },
    { code: 'JP', name: 'Japan', tier: 'JP', tierLabel: '$5' },
    { code: 'KR', name: 'South Korea', tier: 'JP', tierLabel: '$5' },
    { code: 'IL', name: 'Israel', tier: 'JP', tierLabel: '$5' },
    { code: 'IT', name: 'Italy', tier: 'JP', tierLabel: '$5' },

    // $4 tier (FOUR_DOLLAR countries)
    { code: 'TW', name: 'Taiwan', tier: 'TW', tierLabel: '$4' },
    { code: 'ES', name: 'Spain', tier: 'TW', tierLabel: '$4' },
    { code: 'PT', name: 'Portugal', tier: 'TW', tierLabel: '$4' },
    { code: 'PL', name: 'Poland', tier: 'TW', tierLabel: '$4' },

    // $3 tier (THREE_DOLLAR countries)
    { code: 'PH', name: 'Philippines', tier: 'PH', tierLabel: '$3' },
    { code: 'MY', name: 'Malaysia', tier: 'PH', tierLabel: '$3' },
    { code: 'CN', name: 'China', tier: 'PH', tierLabel: '$3' },
    { code: 'AR', name: 'Argentina', tier: 'PH', tierLabel: '$3' },
    { code: 'BR', name: 'Brazil', tier: 'PH', tierLabel: '$3' },

    // $2 tier (TWO_DOLLAR countries)
    { code: 'TH', name: 'Thailand', tier: 'TH', tierLabel: '$2' },
    { code: 'VN', name: 'Vietnam', tier: 'TH', tierLabel: '$2' },
    { code: 'ID', name: 'Indonesia', tier: 'TH', tierLabel: '$2' },
    { code: 'IN', name: 'India', tier: 'TH', tierLabel: '$2' },
    { code: 'NG', name: 'Nigeria', tier: 'TH', tierLabel: '$2' }
  ];
}
